import React, { useMemo } from "react";
import { useQuery } from "urql";
import { keyBy, sortBy } from "lodash";

import { Grid, Column } from "../../../../components/src";
import { FX_NAMES } from "./constants";
import { FoNavInstrumentName } from "./FoNavInstrumentName";
import { GET_FONAV_FX_INSTRUMENTS, GET_FONAV_TIMESERIES } from "./queries";
import { FoNavGridRow, getTimeseriesValue, toFxGridValue, toFxPercentChangeGridValue } from "./utils";

interface FoNavFxProps {
    startDate: string;
    endDate: string;
}

export function FoNavFx({ startDate, endDate }: FoNavFxProps): React.ReactElement {
    const [{ fetching: fetchingInstruments, error: errorInstruments, data: dataInstruments }] = useQuery({
        query: GET_FONAV_FX_INSTRUMENTS,
        variables: { nameIn: FX_NAMES },
        requestPolicy: "cache-and-network"
    });

    const instrumentIds = useMemo(() => {
        if (!dataInstruments?.instruments) {
            return [];
        }
        return dataInstruments.instruments.map((instrument) => instrument._id);
    }, [dataInstruments]);

    const [{ fetching: fetchingStart, error: errorStart, data: dataStart }] = useQuery({
        query: GET_FONAV_TIMESERIES,
        variables: {
            filter: {
                instrumentIdIn: instrumentIds,
                typeIn: "Price(Close)",
                startDate,
                endDate: startDate
            }
        },
        pause: instrumentIds.length === 0 || !startDate,
        requestPolicy: "network-only"
    });

    const [{ fetching: fetchingEnd, error: errorEnd, data: dataEnd }] = useQuery({
        query: GET_FONAV_TIMESERIES,
        variables: {
            filter: {
                instrumentIdIn: instrumentIds,
                typeIn: "Price(Close)",
                startDate: endDate,
                endDate
            }
        },
        pause: instrumentIds.length === 0 || !endDate,
        requestPolicy: "network-only"
    });

    const tableRows = useMemo((): FoNavGridRow[] => {
        if (!dataInstruments?.instruments || !dataStart?.timeseries || !dataEnd?.timeseries) {
            return [];
        }

        const startByInstrumentId = keyBy(
            dataStart.timeseries.filter((series) => series.instrument?._id),
            (series) => series.instrument._id
        );
        const endByInstrumentId = keyBy(
            dataEnd.timeseries.filter((series) => series.instrument?._id),
            (series) => series.instrument._id
        );

        const rows: FoNavGridRow[] = dataInstruments.instruments.map((instrument) => {
            const startRaw = getTimeseriesValue(startByInstrumentId[instrument._id]?.values);
            const endRaw = getTimeseriesValue(endByInstrumentId[instrument._id]?.values);
            return {
                name: instrument.name,
                instrumentId: instrument._id,
                startValue: toFxGridValue(startRaw),
                endValue: toFxGridValue(endRaw),
                change: toFxPercentChangeGridValue(startRaw, endRaw)
            };
        });

        return sortBy(rows, "name");
    }, [dataInstruments, dataStart, dataEnd]);

    if (fetchingInstruments || fetchingStart || fetchingEnd) {
        return <p>Loading FX...</p>;
    }

    if (errorInstruments || errorStart || errorEnd) {
        return <p>error: {JSON.stringify(errorInstruments || errorStart || errorEnd, null, 2)}</p>;
    }

    return (
        <Grid header="FX movements" data={tableRows} className="table-timeseries" tableClassName="table-xs" sortable>
            <Column
                field="name"
                className="nowrap left"
                format={(value: string, item: FoNavGridRow) => (
                    <FoNavInstrumentName name={value} instrumentId={item?.instrumentId} />
                )}
            />
            <Column field="startValue" title="Start value" className="nowrap left" />
            <Column field="endValue" title="End value" />
            <Column field="change" title="Change (%)" />
        </Grid>
    );
}
